/**
 * PILLAR 2 — Measurement History Drawer.
 *
 * Field crews repeatedly asked for it. Lists the last 20 saved
 * measurements on this sheet with:
 *   - tap to recenter on the measurement
 *   - rename (✎)
 *   - delete (✕)
 *
 * The drawer pulls from /sheets/{id}/measurements via the same hook
 * the viewer already uses, and lives behind a "History" trigger so it
 * never competes with the drawing.
 */
import { useEffect, useState } from "react";
import { History, X, Pencil, Trash2, AlertTriangle, Link2, ChevronDown, Package } from "lucide-react";
import { api } from "@/lib/api";
import { measurementDisplayLabel } from "@/lib/units";
import { confirm } from "@/lib/confirm";
import { SendToForeman } from "@/components/SendToForeman";
import ObjectRelations from "@/components/object/ObjectRelations";
import MaterialEstimatePanel from "@/components/MaterialEstimatePanel";

const KIND_LABEL = {
  distance: "Distance",
  polyline: "Polyline",
  area: "Area",
  coordinate: "Coordinate",
};

function fmtWhen(iso) {
  try {
    const dt = new Date(iso);
    const diffMin = Math.floor((Date.now() - dt.getTime()) / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return dt.toLocaleDateString();
  } catch {
    return "—";
  }
}

export function MeasurementHistoryDrawer({ sheetId, reloadKey, onRecenter, onClose, onDeleted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  // R11 continuation (2026-09-10) — which row's Related Objects
  // disclosure is open. Collapsed by default; this drawer already
  // packs rename/delete/recenter into a tight row.
  const [relatedOpenId, setRelatedOpenId] = useState(null);
  // Connected-workflow program (2026-09-10) — which row's Material &
  // Volume Estimate panel is open. Only meaningful for kind==="area".
  const [materialOpenId, setMaterialOpenId] = useState(null);

  useEffect(() => {
    if (!sheetId) return;
    setLoading(true);
    api
      .get(`/sheets/${sheetId}/measurements`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data.slice().reverse().slice(0, 20) : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [sheetId, reloadKey]);

  const rename = async (id) => {
    const newLabel = editLabel.trim();
    if (!newLabel) {
      setEditingId(null);
      return;
    }
    try {
      await api.patch(`/measurements/${id}`, { label: newLabel });
      setRows((prev) => prev.map((m) => (m.id === id ? { ...m, label: newLabel } : m)));
      setEditingId(null);
      confirm.measurementRenamed();
    } catch {
      confirm.saveFailed("Couldn't rename measurement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this measurement?")) return;
    try {
      await api.delete(`/measurements/${id}`);
      setRows((prev) => prev.filter((m) => m.id !== id));
      confirm.measurementDeleted();
      // MEDIUM fix (real-plan cert iteration_184) — notify the viewer so
      // the status-bar MEASURE count and canvas overlay drop this
      // measurement immediately instead of staying stale until reload.
      onDeleted?.();
    } catch {
      confirm.saveFailed("Couldn't delete measurement");
    }
  };

  return (
    <div
      className="fixed right-3 top-20 bottom-3 z-[80] w-[320px] max-w-[92vw] rounded-lg bg-[var(--fop-bg)] border border-[var(--fop-border)] shadow-2xl flex flex-col"
      data-testid="measurement-history-drawer"
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--fop-border)]">
        <div className="inline-flex items-center gap-2">
          <History size={14} className="text-[var(--fop-primary)]" />
          <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--fop-text-2)]">
            Measurement history
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-sm text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] w-7 h-7 flex items-center justify-center"
          data-testid="measurement-history-close"
          aria-label="Close history"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" data-testid="measurement-history-list">
        {loading ? (
          <div className="p-4 text-xs text-[var(--fop-text-3)]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-4 text-xs text-[var(--fop-text-3)] leading-relaxed">
            No measurements yet on this sheet.
            <br />
            Take a distance, polyline, or area measurement and it will land here.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--fop-border)]">
            {rows.map((m) => (
              <li
                key={m.id}
                className="p-2.5 hover:bg-[var(--fop-surface-2)] transition-colors"
                data-testid={`measurement-history-row-${m.id}`}
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={() => onRecenter && onRecenter(m)}
                    className="flex-1 min-w-0 text-left"
                    title="Recenter on this measurement"
                  >
                    <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      {KIND_LABEL[m.kind] || m.kind} · {fmtWhen(m.created_at)}
                    </div>
                    {editingId === m.id ? (
                      <input
                        autoFocus
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onBlur={() => rename(m.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") rename(m.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        className="w-full mt-1 px-1.5 py-0.5 bg-[var(--fop-surface-3)] border border-[var(--fop-primary)] rounded text-[13px] text-[var(--fop-text)]"
                        data-testid={`measurement-history-rename-input-${m.id}`}
                      />
                    ) : (
                      <div className="text-sm font-mono font-semibold text-[var(--fop-text)] truncate">
                        {measurementDisplayLabel(m) || "—"}
                      </div>
                    )}
                    {m.created_by_name && (
                      <div className="text-[10px] text-[var(--fop-text-4)] truncate">
                        {m.created_by_name}
                      </div>
                    )}
                    {m.geometry_integrity_status === "legacy_unverified" && (
                      <div
                        className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warn)]"
                        data-testid={`measurement-legacy-badge-${m.id}`}
                        title={`Re-measurement required — ${m.geometry_unverified_reason || "could not be verified"}`}
                      >
                        <AlertTriangle size={10} /> Legacy · Unverified · Re-measure
                      </div>
                    )}
                    {m.scale_trust === "unverified" && (
                      <div
                        className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warning)]"
                        data-testid={`measurement-scale-unverified-badge-${m.id}`}
                        title={m.scale_trust_reason || "This sheet has more than one real scale and no calibration covers this area — this value may be wrong here"}
                      >
                        <AlertTriangle size={10} /> Scale Unverified
                      </div>
                    )}
                    {m.scale_trust === "untrusted" && (
                      <div
                        className="inline-flex items-center gap-1 mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-danger)]"
                        data-testid={`measurement-scale-untrusted-badge-${m.id}`}
                        title={m.scale_trust_reason || "This sheet is marked NOT TO SCALE — this value was computed only because a human explicitly acknowledged the risk"}
                      >
                        <AlertTriangle size={10} /> Not To Scale
                      </div>
                    )}
                    {m.geometry_recomputed_at && (
                      <div
                        className="mt-1 text-[9px] font-mono uppercase tracking-wider text-[var(--fop-warn)]"
                        data-testid={`measurement-corrected-badge-${m.id}`}
                        title="This value was corrected — an earlier coordinate-model defect made it read wrong"
                      >
                        Corrected → {m.value != null ? `${Number(m.value).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${m.unit || ""}` : "—"}
                        {m.legacy_original_value != null && (
                          <span className="text-[var(--fop-text-4)]"> (was {Number(m.legacy_original_value).toLocaleString(undefined, { maximumFractionDigits: 2 })} {m.unit || ""})</span>
                        )}
                      </div>
                    )}
                  </button>
                  <div className="flex flex-col gap-1 shrink-0">
                    <SendToForeman
                      target={{
                        kind: "measurement",
                        id: m.id,
                        sheet_id: sheetId,
                        label: m.label,
                      }}
                      size="icon"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingId(m.id);
                        setEditLabel(m.label || "");
                      }}
                      className="rounded-sm text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] hover:bg-[var(--fop-surface-3)] w-6 h-6 flex items-center justify-center"
                      data-testid={`measurement-history-rename-${m.id}`}
                      aria-label="Rename"
                      title="Rename"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(m.id);
                      }}
                      className="rounded-sm text-[var(--fop-text-3)] hover:text-[var(--st-escalated)] hover:bg-[var(--fop-surface-3)] w-6 h-6 flex items-center justify-center"
                      data-testid={`measurement-history-delete-${m.id}`}
                      aria-label="Delete"
                      title="Delete"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {/* R11 continuation (2026-09-10) — measurements previously
                    had no way to reach the relationship-engine's Related
                    Objects at all (structures/pipes/conflicts get their
                    own inspector; pins got one via PinQuickActions). This
                    closes the gap for the one remaining object type. */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRelatedOpenId((cur) => (cur === m.id ? null : m.id));
                  }}
                  className="mt-1.5 w-full flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                  data-testid={`measurement-history-related-toggle-${m.id}`}
                >
                  <Link2 size={9} />
                  Related objects
                  <ChevronDown size={10} className={`ml-auto transition-transform ${relatedOpenId === m.id ? "rotate-180" : ""}`} />
                </button>
                {relatedOpenId === m.id && (
                  <div className="mt-1.5" data-testid={`measurement-history-related-content-${m.id}`}>
                    <ObjectRelations objectType="measurement" objectId={m.id} />
                  </div>
                )}
                {(m.kind === "area" || m.kind === "distance" || m.kind === "polyline") && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMaterialOpenId((cur) => (cur === m.id ? null : m.id));
                      }}
                      className="mt-1.5 w-full flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                      data-testid={`measurement-history-material-toggle-${m.id}`}
                    >
                      <Package size={9} />
                      Material &amp; volume estimate
                      <ChevronDown size={10} className={`ml-auto transition-transform ${materialOpenId === m.id ? "rotate-180" : ""}`} />
                    </button>
                    {materialOpenId === m.id && (
                      <div className="mt-1.5" data-testid={`measurement-history-material-content-${m.id}`}>
                        <MaterialEstimatePanel measurementId={m.id} measurementKind={m.kind} />
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-3 py-2 border-t border-[var(--fop-border)] text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
        Showing last {rows.length} of this sheet
      </div>
    </div>
  );
}

export default MeasurementHistoryDrawer;
